/**
 * spineOrder — document-order + depth helpers for spine rows.
 *
 * The spine receives rows in whatever order its producers hand over:
 * gate (execution) order from the doc-type config, ``scoped_module_ids``
 * append order from the REST spine endpoint, or a persisted snapshot's
 * historical order. None of those is document order — for CSR the
 * Finalize gate carries ``2, 13, 14, 15, 16`` so §2 Synopsis trailed
 * §12.6. The writer expects the spine to read like the document's
 * table of contents, so every seam that (re)builds the row list runs
 * it through ``orderSpineSections`` and the row renderer indents by
 * ``spineDepth``.
 *
 * Ordering is the shared ``compareByNumber`` comparator (numeric per
 * segment — "10.1" after "9.8" — with eCTD letter ranking for CTD
 * numbers like "3.2.P.1", and front matter such as "TITLE" / "" / a
 * bare module id leading). Pure; no React, no I/O.
 */

import { compareByNumber, orderKey } from "@shared/client/outlineOrder";

/** Widest indentation step the narrow taskpane can afford. */
export const SPINE_MAX_DEPTH = 4;

/**
 * Return ``rows`` sorted into document order by their display ``number``.
 * Stable: rows that tie (duplicate numbers, several front-matter rows)
 * keep their incoming relative order. Input untouched.
 */
export function orderSpineSections<T extends { number: string }>(rows: readonly T[]): T[] {
  return [...rows].sort(compareByNumber);
}

/**
 * Hierarchy depth of a display number: 1 for front matter ("TITLE", "",
 * bare module ids) and top-level sections ("9"), 2 for "9.3", 3 for
 * "9.3.1", … capped at ``SPINE_MAX_DEPTH``.
 */
export function spineDepth(number: string): number {
  const [bucket, segments] = orderKey(number);
  if (bucket === 0 || segments.length === 0) return 1;
  return Math.min(segments.length, SPINE_MAX_DEPTH);
}
